import endpoints from "./endpoint";
import { sendRequest } from "./sendRequest";

const isFileLike = (value) =>
  (typeof File !== "undefined" && value instanceof File) ||
  (typeof Blob !== "undefined" && value instanceof Blob);

const hasFileValue = (body = {}) => Object.values(body).some(isFileLike);

const buildRequestBodyAndHeaders = (body = {}, accessToken) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  if (!hasFileValue(body)) {
    return {
      body,
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
    };
  }

  const form = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined) return;
    form.append(key, value === null ? "null" : value);
  });

  return { body: form, headers };
};

export const _vendorRegister = async ({ body }) => {
  const url = endpoints.vendorRegister();
  try {
    const response = await sendRequest({ url, method: "POST", body });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _createVendorRegistration = async ({ body, accessToken }) => {
  const url = endpoints.vendorRegistration();
  const headers = accessToken
    ? {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" };

  try {
    const response = await sendRequest({ url, method: "POST", body, headers });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _adminVendorOnboarding = async ({ body, accessToken }) => {
  const url = endpoints.adminVendorOnboarding();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _createOrganization = async ({ body, accessToken }) => {
  const url = endpoints.createOrganization();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body,
      headers: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          }
        : { "Content-Type": "application/json" },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updateOrganization = async ({ organizationId, body, accessToken }) => {
  const url = endpoints.updateOrganization(organizationId);
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getOrganizationByUser = async ({ userId, accessToken }) => {
  const url = endpoints.getOrganizationByUser(userId);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _vendorLogin = async ({ body }) => {
  const url = endpoints.vendorLogin();
  try {
    const response = await sendRequest({ url, method: "POST", body });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _vendorCategories = async () => {
  const url = endpoints.vendorCategories();
  try {
    const response = await sendRequest({ url, method: "GET" });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _botCreation = async ({ body, accessToken, userId }) => {
  const url = endpoints.botCreation(userId);
  try {
    const request = buildRequestBodyAndHeaders(body, accessToken);
    const response = await sendRequest({
      url,
      method: "POST",
      body: request.body,
      headers: request.headers,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getLanguage = async ({ accessToken }) => {
  const url = endpoints.getLanguage();
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getActiveLanguage = async ({ accessToken }) => {
  const url = endpoints.activeLanguage();
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _languageUpdate = async ({ body, accessToken }) => {
  const url = endpoints.languageUpdate();
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _toggleLanguage = async (body, accessToken) => {
  const url = endpoints.toggleLanguage();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _userProfile = async (accessToken) => {
  const url = endpoints.userProfile();
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updateUserProfile = async ({ body, accessToken }) => {
  const url = endpoints.userProfile();
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _organizationDetails = async (accessToken) => {
  const url = endpoints.getOrganizationDetails();
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getChatbotUsers = async ({
  accessToken,
  page = 1,
  limit = 10,
  search = "",
  botId = "",
  organizationId = "",
}) => {
  const params = new URLSearchParams();
  if (page) params.set("page", page);
  if (limit) params.set("limit", limit);
  if (search) params.set("search", search);
  if (botId) params.set("bot_id", botId);
  if (organizationId) params.set("organization_id", organizationId);

  const query = params.toString();
  const url = `${endpoints.chatbotUsers()}${query ? `?${query}` : ""}`;

  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updateBotKnowledgeDetails = async ({ botId, file, url, accessToken }) => {
  const form = new FormData();

  if (file) {
    form.append("knowledge_source_file", file);
  }

  if (url) {
    form.append("url", url);
  }

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const urlWithParams = `${endpoints.updateBotKnowledgeDetails()}?bot_id=${botId}`;

  try {
    const response = await sendRequest({
      url: urlWithParams,
      method: "PATCH",
      body: form,
      headers,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _botKnowledgeStatus = async (token, task_id) => {
  const url = endpoints.botKnowledgeStatus(task_id);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _listBotKnowledgeSources = async ({ botId, accessToken, includeInactive = false }) => {
  const url = `${endpoints.botKnowledgeSources(botId)}?include_inactive=${includeInactive}`;
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _addBotKnowledgeSource = async ({ botId, url, file, text, title, accessToken }) => {
  const form = new FormData();

  // Send exactly one reference (url | knowledge_source_file | text). The caller
  // validates the "exactly one" rule; here we simply forward whatever is set.
  if (url) form.append("url", url);
  if (file) form.append("knowledge_source_file", file);
  if (text) form.append("text", text);
  if (title) form.append("title", title);

  // Authorization only — the browser sets the multipart boundary itself.
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  try {
    const response = await sendRequest({
      url: endpoints.botKnowledgeSources(botId),
      method: "POST",
      body: form,
      headers,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _removeBotKnowledgeSource = async ({ botId, sourceId, accessToken }) => {
  try {
    const response = await sendRequest({
      url: endpoints.botKnowledgeSource(botId, sourceId),
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _retryBotKnowledgeSource = async ({ botId, sourceId, accessToken }) => {
  try {
    const response = await sendRequest({
      url: endpoints.retryBotKnowledgeSource(botId, sourceId),
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _update_bot = async (botId, deatilsToBeUpdated, accessToken) => {
  try {
    const updateBotUrl = `${endpoints.updateBot(botId)}`;
    const request = buildRequestBodyAndHeaders(deatilsToBeUpdated, accessToken);
    const response = await sendRequest({
      url: updateBotUrl,
      method: "PATCH",
      body: request.body,
      headers: request.headers,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getBotDetails = async ({ accessToken, userId }) => {
  const url = endpoints.getBot(userId);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getUserList = async ({ accessToken, page = "", limit = "", search = "" }) => {
  let queryParams = [];

  if (page) queryParams.push(`page=${page}`);
  if (limit) queryParams.push(`limit=${limit}`);
  if (search) queryParams.push(`search=${encodeURIComponent(search)}`);

  const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
  const url = `${endpoints.getUsersList()}${queryString}`;

  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _requestBotOwnerOtp = async ({ botId, email, accessToken }) => {
  const url = endpoints.requestBotOwnerOtp(botId);
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body: { email },
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _verifyBotOwner = async ({ botId, email, otp, accessToken }) => {
  const url = endpoints.linkBotOwner();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body: { bot_id: botId, emails: email, otp },
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _removeBotOwner = async ({ botId, email, ownerId, accessToken }) => {
  const url = endpoints.removeBotOwner(botId);
  const body = ownerId !== undefined && ownerId !== null ? { owner_id: ownerId } : { email };
  try {
    const response = await sendRequest({
      url,
      method: "DELETE",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getPromptDetails = async ({ accessToken, botId }) => {
  const url = endpoints.getPromptDetails(botId);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getApplicablePromptDetails = async ({ accessToken, botId, user_id }) => {
  const url = endpoints.getApplicablePromptDetails(botId, user_id);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updatePromptDetails = async ({ body, accessToken, botId, user_id }) => {
  const url = endpoints.updatePromptDetails(botId, user_id);
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _deactiveVendor = async ({ user_id, isActive, accessToken }) => {
  const url = endpoints.deactivateVendor(user_id);
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body: {
        is_active: isActive,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};
export const _updatePromptSteps = async ({ prompt_id, step_id, body, accessToken }) => {
  const url = endpoints.updatePrompt(prompt_id, step_id); // Ensure that endpoint correctly builds the URL
  try {
    const response = await sendRequest({
      url,
      method: "PUT",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getEscalationRequests = async ({
  bot_id,
  page,
  limit,
  is_read,
  is_resolved,
  room_no,
  accessToken,
}) => {
  const url = endpoints.getEscalationRequests(bot_id, page, limit, is_read, is_resolved, room_no);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updateEscalationRequest = async ({ escalationId, payload, accessToken }) => {
  try {
    const url = `${endpoints.patchEscalationRequests(escalationId)}`;
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const response = await sendRequest({
      url: url,
      method: "PATCH",
      body: { ...payload },
      headers,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _getEscalationHistory = async ({ accessToken, escalationId }) => {
  const url = endpoints.getEscalationHistory(escalationId);
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};
export const _getAllPrompts = async ({ accessToken }) => {
  const url = endpoints.getAllPrompts();
  try {
    const response = await sendRequest({
      url,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _updateAllPrompts = async ({ body, accessToken }) => {
  const url = endpoints.updateAllPrompts();
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _changePassword = async ({ body, accessToken }) => {
  const url = endpoints.changePassword();
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _vendorPassword = async ({ body, accessToken }) => {
  const url = endpoints.vendorPassword();
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _vendorUserName = async ({ body, accessToken, user_id }) => {
  const url = endpoints.vendorUsername(user_id);
  try {
    const response = await sendRequest({
      url,
      method: "PATCH",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _forgotPassword = async ({ body, accessToken }) => {
  const url = endpoints.forgotPassword();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const _resetPassword = async ({ body, accessToken }) => {
  const url = endpoints.resetPassword();
  try {
    const response = await sendRequest({
      url,
      method: "POST",
      body,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};
